/**
 * The signed-in account control in the top bar: who you are, and how to leave.
 *
 * Logout lives here (always visible), not only buried in Settings. Under the
 * dev bypass there is no session to end, so it renders nothing.
 */

import { LogOut, User } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { api, toApiError } from '@/api/client';
import { useSession } from '@/auth/AuthProvider';
import { toast } from '@/components/ui';

export function AccountMenu() {
  const { me, refresh } = useSession();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  // No real session (signed out, or the dev bypass) — nothing to end.
  if (!me?.authenticated || me.bypass) return null;

  const signOut = (all: boolean) => {
    setBusy(true);
    api
      .post(all ? '/auth/logout/all' : '/auth/logout')
      .then(() => {
        setOpen(false);
        refresh(); // the gate re-checks → drops to the login screen
      })
      .catch((e) => toast.error(toApiError(e), 'Could not sign out'))
      .finally(() => setBusy(false));
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={me.email ?? 'Account'}
        className="flex h-7 items-center gap-1.5 rounded border border-border bg-surface-1 px-2 text-[12px] text-hi transition-colors hover:bg-surface-2"
      >
        <User size={13} className="text-mid" aria-hidden />
        <span className="hidden max-w-[150px] truncate lg:inline">{me.email}</span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-1 w-60 rounded-md border border-border bg-surface-1 p-1 shadow-[var(--shadow-pop)]"
        >
          <div className="px-2 pt-1.5 text-[11px] text-lo">Signed in as</div>
          <div className="truncate px-2 pb-2 text-[12px] text-hi">{me.email}</div>
          <div className="mb-1 border-t border-border" />
          <button
            role="menuitem"
            type="button"
            disabled={busy}
            onClick={() => signOut(false)}
            className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-[12px] text-hi hover:bg-surface-2 disabled:opacity-50"
          >
            <LogOut size={13} aria-hidden /> Sign out
          </button>
          <button
            role="menuitem"
            type="button"
            disabled={busy}
            onClick={() => signOut(true)}
            className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-[12px] text-lo hover:bg-surface-2 hover:text-hi disabled:opacity-50"
            title="Revoke every session on every device"
          >
            Sign out everywhere
          </button>
        </div>
      )}
    </div>
  );
}
